import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Undoes the practitioner link: strips `ZOCDOC_PROVIDER_SYSTEM` off whichever practitioner carries
 * this provider, then clears `practitioner_id` from each of the provider's location rows.
 *
 * **Why this has to exist.** The link write only ever appends, so before this endpoint a mis-link
 * was permanent: the wrong practitioner kept claiming `pr_…`, and pointing it at the right person
 * was refused as `provider-has-other-practitioner` with no way out short of editing FHIR by hand.
 * That is tolerable while every link is machine-matched on NPI and tolerable nowhere else.
 *
 * The identifier goes first and the rows second — the reverse of linking — so a run that dies in
 * the middle leaves the provider claimable again rather than claimed by someone who no longer
 * appears in the configuration.
 *
 * An **enabled** link is refused, not silently unmapped: a row that is still accepting Zocdoc
 * bookings must not have its destination removed underneath it. Switch it off first.
 */
export const zocdocUnlinkPractitioner = oc
  .route({ method: 'POST', path: '/v2/zocdoc/provider-links/unlink-practitioner' })
  .input(z.object({ zocdocProviderId: z.string().min(1) }))
  .output(
    z.object({
      status: z.enum([
        /** The identifier was removed, so the provider is claimable again. */
        'unlinked',
        /** No practitioner carried this provider. */
        'not-linked',
      ]),
      /** The practitioner the provider was taken off, when there was one. */
      practitionerId: z.string().optional(),
    }),
  )
